// Imports ------------------------------------------------------------------->

import {Service, service, inject} from "../di";
import {AuthProvider, AccountsUser} from "../../definitions/CommonDefinitions";
import {StorageService} from "../StorageService";
import {AccountsService} from "../AccountsService";
import {EventService} from "../EventService";
import {FacebookAuthenticator} from "../auth/FacebookAuthenticator";
import {FacebookRedirectAuthenticator} from "../auth/FacebookRedirectAuthenticator";
import {GoogleAuthenticator} from "../auth/GoogleAuthenticator";
import {GoogleRedirectAuthenticator} from "../auth/GoogleRedirectAuthenticator";
import {IAuthenticationService, AuthStatus, AuthUser} from "../definitions/IAuthenticationService";
import { ConfigService } from "../ConfigService";

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

@service("defaultAuthenticator")
export class DefaultAuthenticator extends Service implements IAuthenticationService {

	@inject storage: StorageService;
	@inject accountsService: AccountsService;
	@inject eventService: EventService;
	@inject config: ConfigService;

	private initialized_ = false;
	private accountsToken: AccountsUser;
	private authProviders: {
		[key: number]: IAuthenticationService
	};

	constructor() {
		super();
		const postMessageUnsupported = bowser.msie && Number(bowser.version) <= 11;
		this.authProviders = {
			[AuthProvider.GOOGLE]: postMessageUnsupported ? new GoogleRedirectAuthenticator() : new GoogleAuthenticator(),
			[AuthProvider.FACEBOOK]: postMessageUnsupported ? new FacebookRedirectAuthenticator() : new FacebookAuthenticator()
		};
	}

	get status() {
		const provider = this.getAuthProvider_();
		if (this.accountsToken && provider) {
			return AuthStatus.LOGGED_IN;
		} else {

			if (this.accountsToken && !provider) {
				/* Cover off the edge case where the user has logged out with the provider but still has
					* token in localStorage from Accounts API */
				this.accountsToken = null;
				Cookies.remove("flipp-login");
			}

			return AuthStatus.LOGGED_OUT;
		}
	}

	get user() {
		if (this.accountsToken) {
			/* EDGE CASE: There are certain cases where we can't get the user email from the client. For instance,
				* Firefox Private mode blocks ALL access to the Facebook Graph API so we can't fetch the user profile.
				* Accounts returns the email as part of the call to fetchToken, so we'll have it locally cached in the
				* accounts token. We can manually propagate that value through so the UI displays the email. */
			const providerUser = this.getAuthProvider_().user;
			if (!providerUser.info.email) {
				providerUser.info.email = this.accountsToken.email;
			}

			return {
				info: providerUser.info,
				token: this.accountsToken
			} as AuthUser;
		}
	}

	login = (source: AuthProvider, target?: EventTarget): Promise<void> => {
		if (!this.accountsToken) {
			return new Promise<void>((resolve, reject) => {
				this.authProviders[source].login(source, target).then(() => {
					const user = this.authProviders[source].user;
					this.accountsService.fetchToken(user.info).then((resp: AccountsUser) => {
						this.accountsToken = resp;
						Cookies.set("flipp-login", JSON.stringify({token: resp}), {
							expires: 30,
							// domain: this.config.getCookiesDomain()
						});
						resolve();
					}, (e: Error) => {
						reject(e);
					});
				}, (e: Error) => {
					reject(e);
				});
			});
		} else {
			return Promise.resolve(null);
		}
	}

	logout = (target?: EventTarget): Promise<void> => {
		const source = this.getAuthProvider_();
		if (source) {
			return new Promise<void>((resolve, reject) => {
				source.logout(target).then(() => {
					/* Don't call revoke_token on Accounts.
						* See FW-663: Exception when the user is trying to unshare shopping list
						* Problem is that calling revoke_token on web also logs you out of the app. Accounts tokens don't
						* expire, so persist the accounts token indefinitely. */
			//     this.accountsService.revokeToken(this.accountsToken).then(() => {
			//       resolve();
			//     }, (e: Error) => {
			//       reject(e);
			//     });
					this.accountsToken = null;
					Cookies.remove("flipp-login");
					resolve();
				}, (e: Error) => {
					reject(e);
				});
			});
		} else {
			return Promise.resolve(null);
		}
	}

	init = (): Promise<void> => {
		if (this.initialized_) {
			return Promise.resolve(null);
		} else {
			return Promise.all([
				this.authProviders[AuthProvider.GOOGLE].init(),
				this.authProviders[AuthProvider.FACEBOOK].init()
			]).then(() => {
				const authSource = this.getAuthProvider_();
				const login = Cookies.getJSON("flipp-login");
				if (authSource && login) {
					this.accountsToken = login.token;
				} else {
					Cookies.remove("flipp-login");
				}
			}).then(() => {
				/* Asyncronously validate the user token by calling the /user/<id> endpoint.
					* If the stored token is invalid, this call will fail and we can fire a 'logout' event
					* to update the UI */
				if (!this.accountsToken) {
					this.initialized_ = true;
					return;
				}

				this.accountsService.getUser(this.accountsToken).then((data) => {
					this.initialized_ = true;
				}).catch((err: Error) => {
					this.accountsToken = null;
					Cookies.remove("flipp-login");
					this.eventService.fireAuthEvent("logout", null);
					this.initialized_ = true;
				});
			});
		}
	}

	private getAuthProvider_ = () => {
		const sources = Object.keys(this.authProviders);
		for (let idx = 0; idx < sources.length; idx++) {
			const src = this.authProviders[Number(sources[idx])];
			if (src.status === AuthStatus.LOGGED_IN) {
				return src;
			}
		}
	}
}
